import React from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import { getAssetPath } from '../../utils/assetManager';
import brandingConfig from '../../config/brandingConfig';

/**
 * BrandedImage Component
 * 
 * A component for displaying white-labeled images that can be easily swapped
 * when changing branding. This component handles both logos from brandingConfig
 * and other images with appropriate fallbacks.
 */
const BrandedImage = ({
  logoKey,
  imagePath,
  altText,
  width,
  height,
  sx,
  ...props
}) => {
  // Determine the correct source path
  let src;
  let finalAltText = altText;
  
  if (logoKey) {
    // Use a logo from the branding config
    src = getAssetPath(logoKey, true);
    finalAltText = altText || `${brandingConfig.appName} ${logoKey}`;
  } else if (imagePath) {
    // Use a direct image path
    src = getAssetPath(imagePath, false);
    finalAltText = altText || imagePath.split('/').pop().split('.')[0];
  }
  
  if (!src) {
    // Return empty box if no image is found
    return <Box 
      sx={{ 
        width, 
        height, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        ...sx
      }}
      {...props}
    >
      {finalAltText}
    </Box>;
  }
  
  return (
    <Box
      component="img"
      src={src}
      alt={finalAltText}
      sx={{
        width: width || 'auto',
        height: height || 'auto',
        ...sx
      }}
      {...props}
    />
  );
};

BrandedImage.propTypes = {
  /** Key from brandingConfig.logos to use (takes precedence over imagePath) */
  logoKey: PropTypes.string,
  /** Direct path to an image (only used if logoKey is not provided) */
  imagePath: PropTypes.string,
  /** Alt text for the image (defaults to app name + key for logos) */
  altText: PropTypes.string,
  /** Width of the image */
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** Height of the image */
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** Additional MUI sx props */
  sx: PropTypes.object
};

/**
 * Preset BrandedImage for primary logo (with text)
 */
export const PrimaryLogo = (props) => (
  <BrandedImage logoKey="primary" {...props} />
);

/**
 * Preset BrandedImage for icon logo (without text)
 */
export const IconLogo = (props) => (
  <BrandedImage logoKey="icon" {...props} />
);

/**
 * Preset BrandedImage for mobile logo
 */
export const MobileLogo = (props) => (
  <BrandedImage logoKey="mobileLogo" {...props} />
);

export default BrandedImage;